import React, { useState, useEffect, useRef } from 'react';
import useReducedMotion from '../../hooks/useReducedMotion';

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';

export default function ScrambleText({ text, delay = 0, duration = 600, className, onComplete }) {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);
  const rafRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;

    // Reduced motion: show final text immediately
    if (reducedMotion) {
      setDisplayed(text);
      if (onCompleteRef.current) onCompleteRef.current();
      return;
    }

    const startTime = performance.now();
    const charCount = text.length;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      let result = '';
      for (let i = 0; i < charCount; i++) {
        const charProgress = (progress * charCount - i) / 1;
        if (text[i] === ' ') {
          result += ' ';
        } else if (charProgress >= 1) {
          result += text[i];
        } else if (charProgress > 0) {
          result += CHARS[Math.floor(Math.random() * CHARS.length)];
        } else {
          result += CHARS[Math.floor(Math.random() * CHARS.length)];
        }
      }

      setDisplayed(result);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayed(text);
        if (onCompleteRef.current) onCompleteRef.current();
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [started, text, duration, reducedMotion]);

  if (!started) {
    return <span className={className} style={{ visibility: 'hidden' }}>{text}</span>;
  }

  return (
    <span className={className} style={{ position: 'relative', display: 'inline-block' }}>
      <span style={{ visibility: 'hidden' }}>{text}</span>
      <span style={{ position: 'absolute', left: 0, top: 0, width: '100%', textAlign: 'center' }}>{displayed}</span>
    </span>
  );
}
